import React, { useState, useEffect, useRef } from 'react';
import {
  Moon, Sun, Download, Mail, Phone, MapPin, Globe, Linkedin, Car,
  AppWindow, Database, Workflow, Server, ArrowUpRight
} from 'lucide-react';
import './App.css';

const PUBLIC = process.env.PUBLIC_URL;

function CountUp({ to, suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    started.current = false;
    setVal(0);
  }, [to]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / 1200, 1);
          setVal(Math.round((1 - Math.pow(1 - p, 3)) * to));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    // Le PDF ne doit jamais figer un compteur en pleine animation
    const finish = () => { started.current = true; setVal(to); };
    window.addEventListener('beforeprint', finish);
    return () => {
      observer.disconnect();
      window.removeEventListener('beforeprint', finish);
    };
  }, [to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`reveal${visible ? ' in-view' : ''}${className ? ' ' + className : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

// Le PNG de l'avion sert de masque : il prend la couleur du thème, comme sur wingaerosystems.com
function Plane({ className = '' }) {
  const mask = `url(${PUBLIC}/plane.png)`;
  return <span className={`plane ${className}`} aria-hidden="true" style={{ WebkitMaskImage: mask, maskImage: mask }} />;
}

// Logos des produits, repris de leurs sites : l'icône PNG de WingFuel, le LogoMark SVG du header de WingJobs
function ProductLogo({ name }) {
  if (name === 'wingfuel') {
    return <img src={`${PUBLIC}/logos/wingfuel.png`} alt="" className="product-logo" />;
  }
  if (name === 'wingjobs') {
    return (
      <svg className="product-logo product-logo--wingjobs" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
        <path d="M2 18.5 30 4 17.5 26l-3.4-7.1L2 18.5Zm12.8-1.2 3.1 6.4 8-15.9-11.1 9.5Z" />
      </svg>
    );
  }
  return null;
}

function WasWordmark() {
  return <span className="was-wordmark">WING <span className="aero">AERO</span> SYSTEMS</span>;
}

const content = {
  fr: {
    title: "Simon Dumas",
    subtitle: "Data & Software Engineer\nCo-fondateur · Wing Aero Systems",
    download: "Télécharger PDF",
    switchLang: "Switch to English",
    status: "Disponible pour missions",
    contact: "Contact",
    license: "Permis B",
    skills: "Expertise technique",
    skillLead: "Programmation agentique",
    languages: "Langues",
    statLabels: { products: 'produits en service', operators: 'opérateurs suivis', flight: 'h de vol' },
    langItems: [
      { flag: "🇫🇷", name: "Français", level: "Langue maternelle" },
      { flag: "🇬🇧", name: "Anglais", level: "TOEIC 965 pts (mai 2025)\nFCL 055 VFR niveau 5" }
    ],
    sections: {
      profil: {
        title: "Profil",
        text: "Data & software engineer. Alternant à la DGAC (CRNA-Ouest), j'y fiabilise des pipelines de données aéronautiques (XML / AIXM, SQL). Je suis aussi co-fondateur de Wing Aero Systems, qui conçoit des logiciels pour les aéroclubs, les pilotes et les opérateurs. Pilote privé sur mon temps libre."
      },
      services: {
        title: "Ce que je fais",
        tag: "Freelance & Wing Aero Systems · en parallèle de l'alternance DGAC",
        cards: [
          { title: "Applications métier", text: "Web apps pour les processus internes (saisie, suivi, validation), sur ordinateur et mobile. FastAPI, React, PWA." },
          { title: "Data engineering", text: "Collecte, consolidation et contrôle de données : pipelines ETL, tableaux de bord, rapports et exports." },
          { title: "Intégration de systèmes", text: "Connecter les logiciels existants (API, ATS, fichiers XML / CSV) pour supprimer les doubles saisies." },
          { title: "Hébergement & exploitation", text: "VPS mutualisé, Docker, reverse-proxy TLS, sauvegardes chiffrées, supervision et maintenance." }
        ]
      },
      experience: {
        title: "Expérience",
        items: [
          {
            date: "2026 – présent",
            job: "Co-fondateur · Data & Software",
            was: { url: "https://wingaerosystems.com", where: "Brest" },
            intro: "Logiciels et données pour l'aviation. Conception et développement de solutions digitales pour l'industrie aéronautique.",
            products: [
              {
                name: "WingFuel",
                logo: "wingfuel",
                kind: "Produit · En service",
                live: true,
                facts: ["Aéroclub de Brest Finistère", "plein saisi en < 30 s", "exports PDF · Excel · CSV"],
                desc: "Gestion carburant pour aéroclubs. Le pilote scanne un QR code à la cuve, sans compte ni appli ; le trésorier suit le stock de chaque cuve en temps réel, reçoit des alertes de seuil avec prédiction de rupture et exporte la compta. Valorisation au coût moyen pondéré, détection des écarts d'index, journal d'audit, PWA avec notifications push.",
                stack: ["Python", "FastAPI", "PostgreSQL", "React", "TypeScript", "PWA", "Docker"],
                url: "https://wingfuel.fr", label: "wingfuel.fr"
              },
              {
                name: "WingJobs",
                logo: "wingjobs",
                kind: "Produit · En service",
                live: true,
                facts: ["175 opérateurs", "700+ offres ouvertes", "scan toutes les 3 h"],
                desc: "Agrège les offres pilote publiées sur les sites carrière des compagnies, opérateurs ACMI et d'aviation d'affaires (Europe, Golfe, Amérique du Nord), via API d'ATS, scraping et Playwright. Offres normalisées (rôle, QT, base, heures exigées), chaque chiffre extrait gardant la phrase qui le justifie ; détection des nouvelles offres et des postes pourvus, filtrage selon la licence du pilote, alertes email, rapports mensuels du marché.",
                stack: ["Python", "FastAPI", "APScheduler", "Playwright", "SQLite", "React", "Leaflet"],
                url: "https://jobs.wingfuel.fr", label: "jobs.wingfuel.fr"
              }
            ]
          },
          {
            date: "Août 2026 – présent",
            job: "Développeur Full Stack · Freelance",
            company: "France Danse Fédération · fédération nationale",
            link: { url: "https://francedansefederation.com", label: "francedansefederation.com" },
            tasks: [
              "Mission au forfait : cadrage et chiffrage en modules, audit de l'ancien site, évolutions sur demande du client",
              "Site public Astro optimisé SEO, avec une charte graphique créée de zéro",
              "Application métier pour les structures affiliées et la fédération : adhérents et réinscription annuelle, import CSV/Excel avec rapport d'erreurs, licences PDF générées et envoyées automatiquement, inscriptions aux stages et compétitions, tableau de bord et exports",
              "Sécurité & RGPD : minimisation des données, sessions httpOnly avec protection CSRF, limitation des tentatives de connexion, durcissement post-pentest ; CI GitHub Actions, tests pytest, sauvegardes"
            ],
            stack: ["FastAPI", "PostgreSQL", "React / TS", "Astro", "Tailwind", "Docker", "CI/CD"]
          },
          {
            date: "Avril 2026 – présent",
            job: "Data Engineer (alternance)",
            company: "DGAC · CRNA-Ouest, Brest",
            tasks: [
              "Optimisation de l'ETL : refonte complète et industrialisation d'un flux monolithique de traitement de données aéronautiques",
              "Performance : gain majeur via le tuning de requêtes SQL complexes et l'optimisation de scripts Python",
              "Qualité & fiabilité : tests automatisés de cohérence et de validation des données",
              "Infrastructure : exploitation et configuration de ressources serveur dédiées au traitement de données à grande échelle"
            ],
            stack: ["Python", "SQL", "XML / AIXM", "ETL"]
          },
          {
            date: "Avril – juin 2025",
            job: "Développeur Full Stack (stage)",
            company: "DGAC · CRNA-Ouest, Brest",
            tasks: [
              "Application de monitoring temps réel pour la gestion des zones militaires",
              "Flux live : WebSockets pour la synchronisation instantanée des données d'exploitation",
              "Digitalisation : remplacement d'un processus critique manuel par une solution numérique centralisée"
            ],
            stack: ["Python", "WebSockets", "MySQL", "JavaScript"]
          }
        ]
      },
      projets: {
        title: "Autres projets",
        items: [
          { title: "Hub & Wings", desc: "Jeu de gestion aéroportuaire dans le navigateur, simulation déterministe · TypeScript, PixiJS · accès anticipé", url: "https://games.wingfuel.fr" },
          { title: "UBHome", desc: "Hub étudiant : forum, messagerie, calendrier · M1, équipe de 3 · Django, PostgreSQL", url: "https://github.com/SimDms29/UBHome" },
          { title: "java-idl-network-architecture", desc: "Architecture réseau Java · design patterns GoF, JUnit", url: "https://github.com/SimDms29/java-idl-network-architecture" }
        ]
      },
      formation: {
        title: "Formation",
        items: [
          { date: "2025 – 2027", title: "Master ILIADE", school: "UBO · Brest", desc: "Ingénierie du logiciel · M2 en cours" },
          { date: "2022 – 2025", title: "Licence en Informatique", school: "UBO · Brest", desc: "Fondements, algorithmique et bases de données" },
          { date: "2022", title: "Baccalauréat Général", school: "Lycée de l'Iroise · Brest", desc: "Spécialités Mathématiques et Physique, mention Bien" }
        ]
      },
      activites: {
        title: "Aviation",
        ppl: {
          date: "2023",
          title: "Licence de Pilote Privé (PPL)",
          school: "Aéroclub Brest Finistère",
          desc: "140+ heures de vol. Qualifications : EFIS, pas variable, train classique. FCL 055 (anglais radio)."
        }
      },
      references: {
        title: "Références",
        items: [
          { name: "Damien Archer", company: "DGAC · CRNA-Ouest, Pôle DATA", email: "damien.archer@aviation-civile.gouv.fr" }
        ]
      }
    },
    footer: "© 2026 Simon Dumas · Data & Software · Wing Aero Systems"
  },
  en: {
    title: "Simon Dumas",
    subtitle: "Data & Software Engineer\nCo-founder · Wing Aero Systems",
    download: "Download PDF",
    switchLang: "Passer en français",
    status: "Available for projects",
    contact: "Contact",
    license: "Driving licence",
    skills: "Technical expertise",
    skillLead: "Agentic coding",
    languages: "Languages",
    statLabels: { products: 'live products', operators: 'operators tracked', flight: 'flight hrs' },
    langItems: [
      { flag: "🇫🇷", name: "French", level: "Native speaker" },
      { flag: "🇬🇧", name: "English", level: "TOEIC 965 pts (May 2025)\nFCL 055 VFR Level 5 (ICAO)" }
    ],
    sections: {
      profil: {
        title: "Profile",
        text: "Data & software engineer. Apprentice at the French civil aviation authority (DGAC, CRNA-Ouest), where I make aeronautical data pipelines more reliable (XML / AIXM, SQL). I'm also co-founder of Wing Aero Systems, which builds software for flying clubs, pilots and operators. Private pilot in my spare time."
      },
      services: {
        title: "What I do",
        tag: "Freelance & Wing Aero Systems · alongside my DGAC apprenticeship",
        cards: [
          { title: "Business applications", text: "Web apps for internal processes (data entry, tracking, approval), on desktop and mobile. FastAPI, React, PWA." },
          { title: "Data engineering", text: "Collecting, consolidating and checking data: ETL pipelines, dashboards, reports and exports." },
          { title: "Systems integration", text: "Connecting existing software (APIs, ATS, XML / CSV files) to remove duplicate data entry." },
          { title: "Hosting & operations", text: "Shared VPS, Docker, TLS reverse proxy, encrypted backups, monitoring and maintenance." }
        ]
      },
      experience: {
        title: "Experience",
        items: [
          {
            date: "2026 – present",
            job: "Co-founder · Data & Software",
            was: { url: "https://wingaerosystems.com/en/", where: "Brest, France" },
            intro: "Software and data for aviation. Designing and building digital solutions for the aviation industry.",
            products: [
              {
                name: "WingFuel",
                logo: "wingfuel",
                kind: "Product · Live",
                live: true,
                facts: ["Aéroclub de Brest Finistère", "refuel logged in < 30 s", "PDF · Excel · CSV exports"],
                desc: "Fuel management for flying clubs. Pilots scan a QR code at the tank, with no account and no app; the treasurer tracks each tank's stock in real time, gets low-stock alerts with run-out prediction and exports accounting data. Weighted average cost valuation, meter discrepancy detection, audit log, PWA with push notifications.",
                stack: ["Python", "FastAPI", "PostgreSQL", "React", "TypeScript", "PWA", "Docker"],
                url: "https://wingfuel.fr", label: "wingfuel.fr"
              },
              {
                name: "WingJobs",
                logo: "wingjobs",
                kind: "Product · Live",
                live: true,
                facts: ["175 operators", "700+ open positions", "scanned every 3 h"],
                desc: "Aggregates pilot vacancies from the career sites of airlines, ACMI and business aviation operators (Europe, Gulf, North America) through ATS APIs, scraping and Playwright. Listings are normalized (role, type rating, base, required hours) and every extracted figure keeps the sentence that supports it; new and filled positions are detected, jobs are filtered by the pilot's licence, with email alerts and monthly market reports.",
                stack: ["Python", "FastAPI", "APScheduler", "Playwright", "SQLite", "React", "Leaflet"],
                url: "https://jobs.wingfuel.fr", label: "jobs.wingfuel.fr"
              }
            ]
          },
          {
            date: "August 2026 – present",
            job: "Full Stack Developer · Freelance",
            company: "France Danse Fédération · national dance federation",
            link: { url: "https://francedansefederation.com", label: "francedansefederation.com" },
            tasks: [
              "Fixed-price engagement: scoped and quoted in modules, audit of the legacy site, ongoing change requests from the client",
              "SEO-optimized public Astro site, with a visual identity designed from scratch",
              "Business application for affiliated clubs and the federation: members and yearly re-registration, CSV/Excel import with error reports, PDF licences generated and emailed automatically, course and competition registrations, dashboard and exports",
              "Security & GDPR: data minimization, httpOnly sessions with CSRF protection, login rate limiting, post-pentest hardening; GitHub Actions CI, pytest tests, backups"
            ],
            stack: ["FastAPI", "PostgreSQL", "React / TS", "Astro", "Tailwind", "Docker", "CI/CD"]
          },
          {
            date: "April 2026 – present",
            job: "Data Engineer (apprenticeship)",
            company: "DGAC (French ATC) · CRNA-Ouest, Brest",
            tasks: [
              "ETL optimization: complete overhaul and industrialization of a monolithic aeronautical data flow",
              "Performance: significant gains through complex SQL tuning and Python script optimization",
              "Quality & reliability: automated data consistency and validation tests",
              "Infrastructure: running and configuring dedicated server resources for large-scale data processing"
            ],
            stack: ["Python", "SQL", "XML / AIXM", "ETL"]
          },
          {
            date: "April – June 2025",
            job: "Full Stack Developer (internship)",
            company: "DGAC (French ATC) · CRNA-Ouest, Brest",
            tasks: [
              "Real-time monitoring application for military airspace management",
              "Live data: WebSockets for instant synchronization of operational data",
              "Digitalization: replaced a critical manual process with a centralized digital solution"
            ],
            stack: ["Python", "WebSockets", "MySQL", "JavaScript"]
          }
        ]
      },
      projets: {
        title: "Other projects",
        items: [
          { title: "Hub & Wings", desc: "Browser airport management game, deterministic simulation · TypeScript, PixiJS · early access", url: "https://games.wingfuel.fr" },
          { title: "UBHome", desc: "Student hub: forum, messaging, calendar · M1, 3-person team · Django, PostgreSQL", url: "https://github.com/SimDms29/UBHome" },
          { title: "java-idl-network-architecture", desc: "Java network architecture · GoF design patterns, JUnit", url: "https://github.com/SimDms29/java-idl-network-architecture" }
        ]
      },
      formation: {
        title: "Education",
        items: [
          { date: "2025 – 2027", title: "Master's in Computer Science", school: "UBO · Brest", desc: "Software Engineering track · 2nd year" },
          { date: "2022 – 2025", title: "Bachelor's in Computer Science", school: "UBO · Brest", desc: "Algorithms and database foundations" },
          { date: "2022", title: "High School Diploma", school: "Lycée de l'Iroise · Brest", desc: "Math & Physics major, graduated with honors" }
        ]
      },
      activites: {
        title: "Aviation",
        ppl: {
          date: "2023",
          title: "Private Pilot Licence (PPL)",
          school: "Brest Finistère Flying Club",
          desc: "140+ flight hours. Ratings: EFIS, variable pitch, tailwheel. FCL 055 (English proficiency)."
        }
      },
      references: {
        title: "References",
        items: [
          { name: "Damien Archer", company: "DGAC · CRNA-Ouest, Data unit", email: "damien.archer@aviation-civile.gouv.fr" }
        ]
      }
    },
    footer: "© 2026 Simon Dumas · Data & Software · Wing Aero Systems"
  }
};

const skillsList = [
  'Python', 'SQL / PostgreSQL', 'ETL', 'XML / AIXM', 'FastAPI', 'React', 'TypeScript',
  'Playwright', 'Scraping', 'PWA', 'WebSockets', 'Docker', 'Linux / VPS', 'CI/CD'
];

const serviceIcons = [AppWindow, Database, Workflow, Server];

const sectionIds = ['profil', 'services', 'experience', 'projets', 'formation', 'activites', 'references'];

function Stack({ items, lead }) {
  return (
    <ul className="chips">
      {lead && <li className="chip--lead">{lead}</li>}
      {items.map((s, i) => <li key={i}>{s}</li>)}
    </ul>
  );
}

function ProductCard({ p }) {
  return (
    <article className="product-card">
      <div className="product-head">
        <span className={`proj-badge${p.live ? ' proj-badge--live' : ''}`}>{p.kind}</span>
        <a href={p.url} target="_blank" rel="noopener noreferrer" className="product-link">
          {p.label}<ArrowUpRight size={13} className="link-arrow" />
        </a>
      </div>
      <div className="product-title">
        {p.logo && <ProductLogo name={p.logo} />}
        <h4 className="product-name">{p.name}</h4>
      </div>
      <p className="product-facts">{p.facts.map((f, i) => <span key={i}>{f}</span>)}</p>
      <p className="product-desc">{p.desc}</p>
      <Stack items={p.stack} />
    </article>
  );
}

export default function CVApp() {
  const [darkMode, setDarkMode] = useState(true);
  const [lang, setLang] = useState('fr');
  const [activeSection, setActiveSection] = useState('');

  const t = content[lang];
  const s = t.sections;

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      for (const section of sectionIds) {
        const element = document.getElementById(section);
        if (element && scrollPosition >= element.offsetTop && scrollPosition < element.offsetTop + element.offsetHeight) {
          setActiveSection(section);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.className = darkMode ? 'dark-mode' : 'light-mode';
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const sectionClass = (id) => `content-section tile${activeSection === id ? ' active' : ''}`;

  return (
    <div className={`app-container ${darkMode ? 'dark' : 'light'}`}>
      <header className="header">
        <div className="header-content">
          <span className="wordmark">SIMON DUMAS</span>
          <div className="header-actions">
            <button onClick={() => window.print()} className="btn btn--primary">
              <Download size={15} />
              <span className="btn-text">{t.download}</span>
            </button>
            <button
              onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
              className="btn btn--lang"
              title={t.switchLang}
            >
              {lang === 'fr' ? 'EN' : 'FR'}
            </button>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="btn btn--icon"
              aria-label={darkMode ? 'Light mode' : 'Dark mode'}
            >
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>
      </header>

      <div className="main-wrapper">
        <div className="grid-container">
          <aside className="sidebar tile">
            <svg className="sidebar-network" viewBox="0 0 300 150" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
              <path d="M-10 118 L58 92 L112 104 L150 58 L214 74 L262 36 L310 46" />
              <path d="M58 92 L96 40 L150 58" />
              <path d="M150 58 L188 122 L262 108" />
              <circle cx="58" cy="92" r="2" />
              <circle cx="112" cy="104" r="1.6" />
              <circle cx="96" cy="40" r="1.6" />
              <circle className="hub" cx="150" cy="58" r="3" />
              <circle cx="214" cy="74" r="1.6" />
              <circle cx="262" cy="36" r="2" />
              <circle cx="188" cy="122" r="1.6" />
              <circle cx="262" cy="108" r="1.6" />
            </svg>

            <Reveal delay={0}>
              <div className="avatar-container">
                <div className="avatar">
                  <img src={`${PUBLIC}/avatar.JPG`} alt={t.title} className="avatar-image" />
                </div>
                <div className="availability-badge">{t.status}</div>
              </div>
              <h1 className="sidebar-name">{t.title}</h1>
              <p className="sidebar-subtitle">{t.subtitle}</p>
            </Reveal>

            <Reveal delay={120}>
              <div className="sidebar-section">
                <h2 className="label">{t.contact}</h2>
                <a href="https://wingaerosystems.com" target="_blank" rel="noopener noreferrer" className="contact-item">
                  <Globe size={15} className="contact-icon" />
                  <span>wingaerosystems.com</span>
                </a>
                <a href="https://www.linkedin.com/in/simon-dumas-38b8a522b" target="_blank" rel="noopener noreferrer" className="contact-item">
                  <Linkedin size={15} className="contact-icon" />
                  <span>linkedin.com/in/simon-dumas</span>
                </a>
                <a href="mailto:dumassimon22@gmail.com" className="contact-item">
                  <Mail size={15} className="contact-icon" />
                  <span>dumassimon22@gmail.com</span>
                </a>
                <a href="tel:+33769684922" className="contact-item">
                  <Phone size={15} className="contact-icon" />
                  <span>07 69 68 49 22</span>
                </a>
                <div className="contact-item">
                  <MapPin size={15} className="contact-icon" />
                  <span>Brest, France</span>
                </div>
                <div className="contact-item">
                  <Car size={15} className="contact-icon" />
                  <span>{t.license}</span>
                </div>
                <a href="https://cv.wingfuel.fr" target="_blank" rel="noopener noreferrer" className="contact-item contact-item--cv">
                  <ArrowUpRight size={15} className="contact-icon" />
                  <span>cv.wingfuel.fr</span>
                </a>
              </div>
            </Reveal>

            <Reveal delay={220}>
              <div className="stats-strip">
                <div className="stat-item">
                  <span className="stat-value"><CountUp to={2} /></span>
                  <span className="stat-label">{t.statLabels.products}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value"><CountUp to={175} /></span>
                  <span className="stat-label">{t.statLabels.operators}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value"><CountUp to={140} suffix="+" /></span>
                  <span className="stat-label">{t.statLabels.flight}</span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div className="sidebar-section">
                <h2 className="label">{t.skills}</h2>
                <Stack items={skillsList} lead={t.skillLead} />
              </div>
            </Reveal>

            <Reveal delay={380}>
              <div className="sidebar-section sidebar-section--languages">
                <h2 className="label">{t.languages}</h2>
                <div className="languages-container">
                  {t.langItems.map((item, i) => (
                    <div key={i} className="language-item">
                      <div className="language-header">
                        <span>{item.flag}</span>
                        <span className="language-name">{item.name}</span>
                      </div>
                      <p className="language-level">{item.level}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Version papier : formation, aviation et références passent dans la colonne pour tenir sur une page */}
            <div className="print-only">
              <div className="sidebar-section">
                <h2 className="label">{s.formation.title}</h2>
                {s.formation.items.map((form, i) => (
                  <div key={i} className="aside-entry">
                    <span className="aside-date">{form.date}</span>
                    <p className="aside-title">{form.title}</p>
                    <p className="aside-sub">{form.school} · {form.desc}</p>
                  </div>
                ))}
              </div>
              <div className="sidebar-section">
                <h2 className="label">{s.activites.title}</h2>
                <img src={`${PUBLIC}/avion.PNG`} alt="" className="aside-photo" />
                <div className="aside-entry">
                  <span className="aside-date">{s.activites.ppl.date}</span>
                  <p className="aside-title">{s.activites.ppl.title}</p>
                  <p className="aside-sub">{s.activites.ppl.school}</p>
                  <p className="aside-sub">{s.activites.ppl.desc}</p>
                </div>
              </div>
              <div className="sidebar-section sidebar-section--last">
                <h2 className="label">{s.references.title}</h2>
                {s.references.items.map((r, i) => (
                  <div key={i} className="aside-entry">
                    <p className="aside-title">{r.name}</p>
                    <p className="aside-sub">{r.company}</p>
                    <p className="aside-sub">{r.email}</p>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          <main className="main-content">
            <Reveal>
              <section id="profil" className={sectionClass('profil')}>
                <h2 className="section-title">{s.profil.title}</h2>
                <p className="lead">{s.profil.text}</p>
              </section>
            </Reveal>

            <Reveal>
              <section id="experience" className={sectionClass('experience')}>
                <h2 className="section-title">{s.experience.title}</h2>
                <div className="timeline-list-container">
                  {s.experience.items.map((exp, i) => (
                    <Reveal key={i} delay={i * 100}>
                      <div className="timeline">
                        <div className={`timeline-dot${i === 0 ? '' : ' muted'}`} />
                        <div className="timeline-content">
                          <span className="timeline-badge">{exp.date}</span>
                          <h3 className="timeline-title">{exp.job}</h3>
                          {exp.was ? (
                            <a href={exp.was.url} target="_blank" rel="noopener noreferrer" className="was-mark">
                              <Plane />
                              <WasWordmark />
                              <span className="was-where">{exp.was.where}</span>
                            </a>
                          ) : (
                            <p className="timeline-subtitle">
                              {exp.company}
                              {exp.link && (
                                <>
                                  {' · '}
                                  <a href={exp.link.url} target="_blank" rel="noopener noreferrer" className="inline-link">
                                    {exp.link.label}<ArrowUpRight size={12} className="link-arrow" />
                                  </a>
                                </>
                              )}
                            </p>
                          )}
                          {exp.intro && <p className="timeline-description">{exp.intro}</p>}
                          {exp.tasks && (
                            <ul className="timeline-list">
                              {exp.tasks.map((task, j) => <li key={j}>{task}</li>)}
                            </ul>
                          )}
                          {exp.stack && <Stack items={exp.stack} />}
                          {exp.products && (
                            <div className="products">
                              {exp.products.map((p, j) => <ProductCard key={j} p={p} />)}
                            </div>
                          )}
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </section>
            </Reveal>

            <Reveal className="no-print">
              <section id="services" className={sectionClass('services')}>
                <h2 className="section-title">{s.services.title}</h2>
                <p className="section-tag">{s.services.tag}</p>
                <div className="services-grid">
                  {s.services.cards.map((card, i) => {
                    const Icon = serviceIcons[i];
                    return (
                      <Reveal key={i} delay={i * 80} className="service-reveal">
                        <div className="service-card">
                          <Icon className="service-icon" size={20} strokeWidth={1.6} />
                          <h3 className="service-title">{card.title}</h3>
                          <p className="service-text">{card.text}</p>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              </section>
            </Reveal>

            <Reveal className="no-print">
              <section id="formation" className={sectionClass('formation')}>
                <h2 className="section-title">{s.formation.title}</h2>
                <div className="timeline-list-container timeline-list-container--tight">
                  {s.formation.items.map((form, i) => (
                    <Reveal key={i} delay={i * 80}>
                      <div className="timeline">
                        <div className={`timeline-dot${i === 0 ? '' : ' muted'}`} />
                        <div className="timeline-content">
                          <span className="timeline-badge">{form.date}</span>
                          <h3 className="timeline-title">{form.title}</h3>
                          <p className="timeline-subtitle">{form.school}</p>
                          <p className="timeline-description">{form.desc}</p>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </section>
            </Reveal>

            <Reveal className="no-print">
              <section id="activites" className={sectionClass('activites')}>
                <h2 className="section-title">{s.activites.title}</h2>
                <div className="activites-layout">
                  <div className="timeline">
                    <div className="timeline-dot" />
                    <div className="timeline-content">
                      <span className="timeline-badge">{s.activites.ppl.date}</span>
                      <h3 className="timeline-title">{s.activites.ppl.title}</h3>
                      <p className="timeline-subtitle">{s.activites.ppl.school}</p>
                      <p className="timeline-description">{s.activites.ppl.desc}</p>
                    </div>
                  </div>
                  <div className="avion-photo-wrapper">
                    <img src={`${PUBLIC}/avion.PNG`} alt="" className="avion-photo" />
                  </div>
                </div>
              </section>
            </Reveal>

            <Reveal>
              <section id="projets" className={sectionClass('projets')}>
                <h2 className="section-title">{s.projets.title}</h2>
                <ul className="other-list">
                  {s.projets.items.map((p, i) => (
                    <li key={i}>
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="other-item">
                        <span className="other-name">{p.title}</span>
                        <span className="other-desc">{p.desc}</span>
                        <ArrowUpRight size={15} className="other-arrow" />
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            </Reveal>

            <Reveal className="no-print">
              <section id="references" className={sectionClass('references')}>
                <h2 className="section-title">{s.references.title}</h2>
                {s.references.items.map((r, i) => (
                  <div key={i} className="reference-card">
                    <h3 className="reference-name">{r.name}</h3>
                    <p className="reference-company">{r.company}</p>
                    <a href={`mailto:${r.email}`} className="reference-info">{r.email}</a>
                  </div>
                ))}
              </section>
            </Reveal>
          </main>
        </div>
      </div>

      <footer className="footer">
        <div className="footer-content">
          <Plane className="footer-plane" />
          <p className="footer-text">{t.footer}</p>
        </div>
      </footer>
    </div>
  );
}
